import React, {useState, createRef} from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    Flex,
    Icon,
    Text,
    useToast,
    Alert,
} from '@spothero/ui';
import {useForm, Controller} from 'react-hook-form';
import IconExclamationCircle from '@spothero/icons/exclamation-circle-filled';
import {IMaskMixin} from 'react-imask';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {updatePhone} from 'store/parking-pass/parking-pass-actions';
import trackReservationUpdated, {
    LOCATIONS,
    UPDATED_FIELDS,
} from 'segment/events/reservation-updated';
import {PROPTYPE_RESERVATION} from '@/proptypes/reservation-prop-types';

const IMaskInput = IMaskMixin(({inputRef, ...props}) => (
    <Input ref={inputRef} {...props} />
));

const PHONE_FIELD = 'phone';

const ChangePhoneModal = ({
    reservation,
    isOpen,
    onClose,
    hidePhoneNumber,
    activeStatus,
}) => {
    const {
        control,
        handleSubmit,
        formState: {errors, isSubmitting, dirtyFields},
        reset,
        resetField,
    } = useForm({
        defaultValues: {
            [PHONE_FIELD]: hidePhoneNumber ? '' : reservation.phoneNumber,
        },
    });

    const user = useSelector(state => state.user.data);
    const dispatch = useDispatch();
    const [submissionError, setSubmissionError] = useState(null);
    const toast = useToast();

    const addToast = () => {
        const toastRef = createRef();

        const close = () => {
            if (toastRef.current) {
                toast.close(toastRef.current);
            }
        };

        toastRef.current = toast({
            position: 'top',
            duration: 3000,
            render: () => (
                <Alert
                    title="Phone number updated"
                    status="success"
                    showIcon
                    showDismiss
                    onDismissClick={close}
                />
            ),
        });
    };

    const handleClose = () => {
        onClose();
        setTimeout(() => {
            reset();
            setSubmissionError(null);
        }, 100);
    };

    const onSubmit = async ({phone}) => {
        setSubmissionError(false);
        const response = await dispatch(
            updatePhone({
                phoneNumber: phone,
            })
        );

        if (response.success) {
            resetField(PHONE_FIELD, {defaultValue: phone});
            trackReservationUpdated({
                reservation,
                updatedProperties: [UPDATED_FIELDS.PHONE],
                location: LOCATIONS.PARKING_PASS,
                activeStatus,
                user,
            });
            handleClose();
            addToast();
        } else {
            setSubmissionError(true);
        }
    };

    return (
        <Modal
            hasHeader
            hasFooter
            size="sm"
            isOpen={isOpen}
            onClose={handleClose}
        >
            <ModalHeader>Change Phone Number</ModalHeader>
            <ModalBody>
                <Flex width="100%" flexDirection="column">
                    <form onSubmit={handleSubmit(onSubmit)} id="phone-form">
                        <Controller
                            name={PHONE_FIELD}
                            control={control}
                            rules={{
                                required: true,
                                minLength: 10,
                                maxLength: 10,
                            }}
                            render={({field: {onChange, ...fieldProps}}) => (
                                <IMaskInput
                                    id={PHONE_FIELD}
                                    label="Phone"
                                    mask="(000) 000-0000"
                                    unmask
                                    isInvalid={Object.keys(errors).length > 0}
                                    isDisabled={isSubmitting}
                                    errorMessage="Please enter a valid 10 digit phone number."
                                    {...fieldProps}
                                    onChange={null}
                                    onAccept={onChange}
                                />
                            )}
                        />
                    </form>
                    {/* TODO: Replace with Notification once in fe-ui */}
                    {submissionError ? (
                        <Flex
                            paddingY={3}
                            paddingX={4}
                            background="red.100"
                            alignItems="center"
                            borderRadius="md"
                            marginTop={4}
                        >
                            <Icon
                                width={6}
                                height="auto"
                                color="error"
                                marginRight={2}
                                as={IconExclamationCircle}
                            />
                            <Text fontSize="sm">
                                Oops, we hit a snag saving your phone number.
                                Please try again.
                            </Text>
                        </Flex>
                    ) : null}
                </Flex>
            </ModalBody>
            <ModalFooter>
                <Button
                    onClick={handleClose}
                    variant="secondary"
                    marginRight={4}
                    fontFamily="body"
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                <Button
                    form="phone-form"
                    type="submit"
                    variant="primary"
                    fontFamily="body"
                    disabled={isSubmitting || !dirtyFields[PHONE_FIELD]}
                >
                    Save
                </Button>
            </ModalFooter>
        </Modal>
    );
};

ChangePhoneModal.propTypes = {
    reservation: PROPTYPE_RESERVATION.isRequired,
    isOpen: PropTypes.bool.isRequired,
    hidePhoneNumber: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    activeStatus: PropTypes.string.isRequired,
};

export default ChangePhoneModal;
